import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FeedbackContext, FeedbackKind, feedbackClient } from '../client/feedbackClient';
import { RobbieState } from '../components/ui';

/**
 * Everything the feedback panel does, so the panel itself is only JSX.
 *
 * Three steps: pick a kind, write, done. Robbie's mark follows along — idle
 * while choosing, listening while the person types, done once it is sent —
 * because the mark is the only thing in the panel that can say "I'm reading
 * this" without a sentence saying so.
 *
 * The thank-you closes itself: the panel has done its job and nothing in it
 * is worth a second click.
 */

export type FeedbackStep = 'choose' | 'write' | 'sent';

/** How long the thank-you stays before the panel bows out. */
const THANK_YOU_MS = 3_200;

/** Typing goes quiet this long before the mark stops listening. */
const LISTENING_IDLE_MS = 1_200;

export const MAX_MESSAGE_CHARS = 2000;

export interface FeedbackFormState {
  step: FeedbackStep;
  kind: FeedbackKind | null;
  message: string;
  robbieState: RobbieState;
  sending: boolean;
  /** Robbie's words when a send failed; the message is never lost. */
  error: string | null;
  context: FeedbackContext;
  chooseKind: (kind: FeedbackKind) => void;
  back: () => void;
  setMessage: (message: string) => void;
  send: () => void;
  canSend: boolean;
}

/** The in-app route, never the full URL — a query string could carry anything. */
const pathOf = (pathname: string): string => pathname || '/';

/**
 * The human RFQ id out of the path, so a report from inside an RFQ names it
 * without the person having to. Read from the URL rather than `useParams`
 * because the panel is mounted app-wide, outside any matched route.
 */
const rfqIdOf = (pathname: string): string | undefined =>
  /^\/rfqs\/([^/]+)/.exec(pathname)?.[1];

const viewportOf = (): string =>
  typeof window === 'undefined' ? 'unknown' : `${window.innerWidth}x${window.innerHeight}`;

const userAgentOf = (): string =>
  typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent;

/**
 * @param onDone - called after the thank-you has been read; closes the panel
 */
export const useFeedbackForm = (onDone: () => void): FeedbackFormState => {
  const location = useLocation();

  const [step, setStep] = useState<FeedbackStep>('choose');
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [message, setMessageState] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);

  const typingTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  // `onDone` is recreated by the parent on every render; holding it in a ref
  // keeps the auto-close timer from restarting and never firing.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(
    () => () => {
      window.clearTimeout(typingTimer.current);
      window.clearTimeout(closeTimer.current);
    },
    []
  );

  const chooseKind = useCallback((next: FeedbackKind) => {
    setKind(next);
    setStep('write');
    setError(null);
  }, []);

  const back = useCallback(() => {
    setStep('choose');
    setError(null);
  }, []);

  const setMessage = useCallback((next: string) => {
    setMessageState(next.slice(0, MAX_MESSAGE_CHARS));
    setTyping(true);
    window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => setTyping(false), LISTENING_IDLE_MS);
  }, []);

  const rfqId = rfqIdOf(location.pathname);
  const context: FeedbackContext = {
    path: pathOf(location.pathname),
    ...(rfqId ? { rfqId } : {}),
    userAgent: userAgentOf(),
    viewport: viewportOf(),
  };

  const canSend = Boolean(kind) && message.trim().length > 0 && !sending;

  const send = useCallback(() => {
    if (!kind || message.trim().length === 0 || sending) return;

    setSending(true);
    setError(null);
    feedbackClient
      .submit({ kind, message: message.trim(), context })
      .then(() => {
        setStep('sent');
        setTyping(false);
        closeTimer.current = window.setTimeout(() => onDoneRef.current(), THANK_YOU_MS);
      })
      .catch(() => {
        // The message stays in the box — losing what someone wrote about a
        // bug would be its own bug.
        setError("That didn't reach me — your words are still here. Try Send again in a moment.");
      })
      .finally(() => setSending(false));
    // `context` is rebuilt every render from the current route; depending on it
    // would recreate `send` constantly without changing what it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, message, sending]);

  const robbieState: RobbieState =
    step === 'sent' ? 'done' : typing || sending ? 'listening' : 'idle';

  return {
    step, kind, message, robbieState, sending, error, context,
    chooseKind, back, setMessage, send, canSend,
  };
};

export default useFeedbackForm;
